import React, { useEffect, useState } from "react";
import BusinessApiService from "../../services/BusinessApiService";
import { getCurrentBusinessId } from "../../services/api";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function BusinessGS1Page() {
  const [business, setBusiness] = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const token   = BusinessApiService.getBusinessToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // GS1 prefix submission
  const [showSubmit, setShowSubmit]       = useState(false);
  const [submitPrefix, setSubmitPrefix]   = useState("");
  const [submitMember, setSubmitMember]   = useState("");
  const [submitting, setSubmitting]       = useState(false);
  const [submitMsg, setSubmitMsg]         = useState<{text:string;type:'success'|'error'}|null>(null);

  useEffect(() => {
    const id = getCurrentBusinessId();
    if (!id) { setError("No business ID"); setLoading(false); return; }
    fetch(`${API_BASE}/business/${id}/public`, { headers })
      .then(r => r.json())
      .then(d => { setBusiness(d.business || d.data || d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  if (error) return (
    <div className="p-6 text-red-600 bg-red-50 rounded-xl">⚠️ {error}</div>
  );

  const gs1 = business?.gs1;

  const submitGs1Request = async () => {
    if (submitPrefix.trim().length < 6) { alert("Enter a valid GS1 prefix (6-9 digits)"); return; }
    setSubmitting(true); setSubmitMsg(null);
    try {
      const id = getCurrentBusinessId();
      const res = await fetch(`${API_BASE}/business/${id}/gs1-request`, {
        method: 'POST', headers,
        body: JSON.stringify({ companyPrefix: submitPrefix.trim(), memberNumber: submitMember.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitMsg({ text: "✅ GS1 prefix submitted! Admin will review within 1-2 business days.", type: 'success' });
        setShowSubmit(false);
        // Reload business data
        const id2 = getCurrentBusinessId();
        fetch(`${API_BASE}/business/${id2}/public`, { headers })
          .then(r => r.json()).then(d => setBusiness(d.business || d));
      } else {
        setSubmitMsg({ text: data.message || 'Submission failed', type: 'error' });
      }
    } catch (e: any) { setSubmitMsg({ text: e.message, type: 'error' }); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6">

      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">GS1 Settings</h2>
        <p className="text-gray-500 text-sm">{business?.companyName}</p>
      </div>

      {/* GS1 Status Card */}
      <div className={`rounded-xl p-6 border ${gs1 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">{gs1 ? '✅' : '⚠️'}</span>
          <div>
            <h3 className="font-semibold text-gray-900">
              {gs1 ? 'GS1 Identity Assigned' : 'No GS1 Identity'}
            </h3>
            <p className="text-sm text-gray-500">
              {gs1 ? 'Your business has a GS1 GLN and company prefix' : 'Contact JepTags to assign GS1 identity'}
            </p>
          </div>
        </div>

        {gs1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-xs text-gray-500 mb-1">Company Prefix</p>
              <p className="font-mono text-lg font-bold text-blue-700">{gs1.companyPrefix || '—'}</p>
              <p className="text-xs text-gray-400 mt-1">
                {gs1.isOwnPrefix ? '✅ Your own GS1 prefix' : '🔵 JepTags platform prefix'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-xs text-gray-500 mb-1">GLN (Global Location Number)</p>
              <p className="font-mono text-xs font-bold text-green-700 break-all">{gs1.gln || '—'}</p>
              <p className="text-xs text-gray-400 mt-1">Identifies your business location globally</p>
            </div>
            {gs1.memberNumber && (
              <div className="bg-white rounded-lg p-4 border">
                <p className="text-xs text-gray-500 mb-1">GS1 Member Number</p>
                <p className="font-mono text-lg font-bold text-purple-700">{gs1.memberNumber}</p>
              </div>
            )}
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-xs text-gray-500 mb-1">Assigned At</p>
              <p className="text-sm font-medium text-gray-700">
                {gs1.generatedAt ? new Date(gs1.generatedAt).toLocaleDateString() : '—'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* GS1 Phases */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">GS1 Integration Phases</h3>
        <div className="space-y-4">
          {[
            {
              phase: 1,
              title: "Platform Prefix (Current)",
              desc: "JepTags assigns GTINs using our company prefix. Products are traceable on JepTags platform.",
              status: "active",
              icon: "🔵",
            },
            {
              phase: 2,
              title: "Own GS1 Prefix",
              desc: "Register your own GS1 company prefix. GTINs are globally recognized and scannable anywhere.",
              status: gs1?.isOwnPrefix ? "active" : "available",
              icon: "🟡",
            },
            {
              phase: 3,
              title: "GS1 Solution Partner",
              desc: "Bulk GTIN purchasing at discounted rates. Full EDI integration with retailers.",
              status: "future",
              icon: "⚪",
            },
          ].map(p => (
            <div key={p.phase} className={`flex gap-4 p-4 rounded-lg border ${
              p.status === 'active' ? 'bg-blue-50 border-blue-200' :
              p.status === 'available' ? 'bg-yellow-50 border-yellow-200' :
              'bg-gray-50 border-gray-200'
            }`}>
              <span className="text-2xl mt-0.5">{p.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Phase {p.phase}: {p.title}</span>
                  {p.status === 'active' && (
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">Active</span>
                  )}
                  {p.status === 'available' && (
                    <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">Available</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* GS1 Prefix Submission */}
      {!gs1?.isOwnPrefix && (
        <div className="bg-white border rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Have your own GS1 prefix?</h3>

          {/* Pending status */}
          {gs1?.verificationStatus === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-yellow-800">⏳ Prefix Pending Review</p>
              <p className="text-sm text-yellow-700 mt-1">
                Your prefix <span className="font-mono font-bold">{gs1.pendingPrefix}</span> is being reviewed by JepTags admin.
              </p>
              <p className="text-xs text-yellow-600 mt-1">Usually approved within 1-2 business days.</p>
            </div>
          )}

          {/* Rejected status */}
          {gs1?.verificationStatus === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-red-800">❌ Prefix Request Rejected</p>
              <p className="text-sm text-red-700 mt-1">Reason: {gs1.rejectionReason || 'Could not verify prefix ownership'}</p>
              <p className="text-xs text-red-600 mt-1">You can submit a new request below.</p>
            </div>
          )}

          {submitMsg && (
            <div className={`p-3 rounded-lg text-sm mb-4 ${submitMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {submitMsg.text}
            </div>
          )}

          {gs1?.verificationStatus !== 'pending' && !showSubmit && (
            <div>
              <p className="text-sm text-gray-500 mb-4">
                If you have a GS1 company prefix, submit it for admin verification. Once approved, your products will use your own GTINs.
              </p>
              <button onClick={() => setShowSubmit(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                🏷️ Submit My GS1 Prefix
              </button>
            </div>
          )}

          {showSubmit && (
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <p className="text-sm font-semibold text-blue-900 mb-3">Submit GS1 Prefix for Verification</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GS1 Company Prefix *</label>
                  <input type="text" value={submitPrefix} onChange={e => setSubmitPrefix(e.target.value.replace(/\D/g, ''))}
                    placeholder="e.g. 0614142" maxLength={9}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500" />
                  <p className="text-xs text-gray-400 mt-1">6-9 digit prefix from your GS1 membership</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GS1 Member Number</label>
                  <input type="text" value={submitMember} onChange={e => setSubmitMember(e.target.value)}
                    placeholder="e.g. GS1US-12345"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                  <p className="text-xs text-gray-400 mt-1">Optional but helps verification</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={submitGs1Request} disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Submitting...' : '📤 Submit for Review'}
                </button>
                <button onClick={() => setShowSubmit(false)}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* What consumers see */}
      <div className="bg-gray-50 border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 What consumers see when scanning</h3>
        <div className="bg-white rounded-lg p-4 border text-sm space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Brand/Manufacturer</span>
            <span className="font-medium">{business?.companyName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">GS1 Verified</span>
            <span className="text-green-600 font-medium">✅ Yes</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Traceability</span>
            <span className="text-blue-600 font-medium">🔗 EPCIS Enabled</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Location</span>
            <span className="font-medium">{business?.address?.city}, {business?.address?.country}</span>
          </div>
        </div>
      </div>

    </div>
  );
}